import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormControl,
} from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { LabelService } from '../../../services/label.service';
import { InvoiceCompanyClient, InvoicePatient } from '../../../models/invoices';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CountryService } from '../country.service';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-invoices-company-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatIconModule,
    MatSlideToggleModule,
    MatSelectModule,
    FormsModule,
    MatTooltipModule,
  ],
  templateUrl: './invoices-company-panel.component.html',
  styleUrl: './invoices-company-panel.component.scss',
})
export class InvoicesCompanyPanelComponent implements OnInit {
  @Input() client!: InvoiceCompanyClient;
  @Input() patients: InvoicePatient[] = [];
  @Input() newCompanyPanelTitle: string = 'Nuovo Cliente Azienda';
  @Output() closePanel: EventEmitter<void> = new EventEmitter<void>();
  @Output() createClient = new EventEmitter<InvoiceCompanyClient>();
  @Output() updateClient = new EventEmitter<InvoiceCompanyClient>();

  isEditClient: boolean = false;
  clientForm: FormGroup;
  selectedPatient: InvoicePatient | null = null;
  showPatientsList: boolean = false;
  countries: { name: string; code: string }[] = [];
  filteredCountries: { name: string; code: string }[] = [];
  filteredPatients: InvoicePatient[] = [];
  patientSearchText: string = '';

  constructor(
    public labelService: LabelService,
    private fb: FormBuilder,
    private countryService: CountryService
  ) {
    this.countries = [{ name: 'Italia', code: 'IT' }];
    this.filteredCountries = this.countries;
    this.clientForm = this.fb.group({
      is_patient: [false],
      company_name: ['', Validators.required],
      tax_code: ['', Validators.required],
      vat_number: [
        '',
        [
          Validators.required,
          Validators.pattern(/^IT\d{11}$/),
          Validators.minLength(13),
          Validators.maxLength(13),
        ],
      ],
      unique_code: [
        '',
        [Validators.required, Validators.minLength(7), Validators.maxLength(7)],
      ],
      email: ['', Validators.email],
      country: ['', Validators.required],
      city: ['', Validators.required],
      province: ['', Validators.required],
      street_address: ['', Validators.required],
      street_number: ['', Validators.required],
      zip_code: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.filteredPatients = this.sortPatients([...this.patients]);

    if (Object.keys(this.client).length > 0) {
      if (this.client.id !== '') {
        this.isEditClient = true;
      }

      const clientWithCountryName = {
        ...this.client,
        country: this.countryService.getNameFromCode(this.client.country),
      };

      this.clientForm.patchValue(clientWithCountryName);

      if (
        this.client.is_patient &&
        this.client.patient_first_name &&
        this.client.patient_last_name
      ) {
        this.selectedPatient = {
          id: this.client.patient_id || '',
          first_name: this.client.patient_first_name,
          last_name: this.client.patient_last_name,
          tax_code: this.client.tax_code,
          email: this.client.email,
          number: '',
        };
        this.patientSearchText = `${this.client.patient_first_name} ${this.client.patient_last_name}`;
      }
    }
  }

  filterCountries(event: any) {
    const searchText = event.target.value.toLowerCase();
    this.filteredCountries = this.countries.filter((country) =>
      country.name.toLowerCase().includes(searchText)
    );
  }

  selectPatient(patient: InvoicePatient) {
    this.selectedPatient = patient;
    this.patientSearchText = `${patient.first_name} ${patient.last_name}`;
    this.showPatientsList = false;
    if (patient.tax_code || patient.email) {
      this.clientForm.patchValue({
        tax_code: patient.tax_code,
        email: patient.email,
      });
    }
  }

  togglePatientsList() {
    this.showPatientsList = !this.showPatientsList;
    if (this.showPatientsList) {
      this.filterPatients(this.patientSearchText || '');
    }
  }

  onCancel() {
    this.closePanel.emit();
  }

  onConfirm() {
    if (this.clientForm.valid) {
      const formValue = this.clientForm.value;
      const countryCode = this.countryService.getCodeFromName(
        formValue.country
      );
      const updatedClient: InvoiceCompanyClient = {
        ...formValue,
        country: countryCode,
        patient_id: `${this.selectedPatient?.id}`,
        patient_first_name: this.selectedPatient?.first_name,
        patient_last_name: this.selectedPatient?.last_name,
        id: this.client?.id || '',
      };
      if (this.isEditClient) {
        this.updateClient.emit(updatedClient);
      } else {
        this.createClient.emit(updatedClient);
      }
    }
  }

  filterPatients(searchText: string) {
    this.patientSearchText = searchText;
    if (!searchText || searchText.trim() === '') {
      this.filteredPatients = this.sortPatients([...this.patients]);
    } else {
      this.filteredPatients = this.sortPatients(
        this.patients.filter((patient) =>
          `${patient.first_name} ${patient.last_name}`
            .toLowerCase()
            .includes(searchText.toLowerCase())
        )
      );
    }
  }

  sortPatients(patients: InvoicePatient[]): InvoicePatient[] {
    return patients.sort((a, b) => a.last_name.localeCompare(b.last_name));
  }

  onInputClick(event: Event) {
    event.stopPropagation();
    this.showPatientsList = true;
    this.filterPatients(this.patientSearchText || '');
  }
}
